import React from 'react';
import { IPositionSummary } from '../../../../Models/IPositionSummary';
import { NavigationPath, NavItem } from './BlindTestDashboardTypes';
import { getBlindTestDashboardGlobalStyles } from './BlindTestDashboardStyles';
import { EmptyResultsDisplay } from './BlindTestDashboardComponents';

/**
 * Get a unique path identifier for navigation items
 */
export const getPathIdentifier = (level: number, path: NavigationPath, itemName: string): string => {
  switch(level) {
    case 0: return itemName;
    case 1: return `${path.industry}/${itemName}`;
    case 2: return `${path.industry}/${path.subIndustry}/${itemName}`;
    case 3: return `${path.industry}/${path.subIndustry}/${path.application}/${itemName}`;
    default: return itemName;
  }
};

/**
 * Generate consistent card ID for position summary items
 */
export const generateCardId = (item: IPositionSummary): string => {
  return `${item.alfaLavalPosition}-${item.productLine}-${item.blindTestDataSetId}`;
};

/**
 * Check if a card can be downloaded
 */
export const canDownloadCard = (item: IPositionSummary): boolean => {
  return item.summary["todo"] === 0;
};

/**
 * Check if a card is clickable for a given user type
 */
export const isCardClickable = (item: IPositionSummary, isModeler: boolean, isSme: boolean): boolean => {
  return !(isModeler && !isSme && item.summary["todo"] > 0);
};

/**
 * Get completed count for a position summary item
 */
export const getCompletedCount = (item: IPositionSummary): number => {
  return (item.summary["emelie - bts"] || 0) +
         (item.summary["emelie - acceptable"] || 0) +
         (item.summary["sme"] || 0);
};

/**
 * Get todo count for a position summary item
 */
export const getTodoCount = (item: IPositionSummary): number => {
  return item.summary["todo"] || 0;
};

/**
 * Parse navigation item name to get heading and subheading
 */
export const parseNavigationItemName = (name: string): { heading: string; subHeading: string } => {
  const tokens = name.split("-");
  const heading = tokens[0]?.trim() || "";
  const subHeading = tokens.length > 1 ? tokens[1]?.trim() : "";
  return { heading, subHeading };
};

/**
 * Check if navigation item should be expanded
 */
export const shouldNavigationItemBeExpanded = (
  item: NavItem,
  level: number,
  pathIdentifier: string,
  expandedItems: Record<string, boolean>,
  selectedPaths: {
    industry?: string;
    subIndustry?: string;
    application?: string;
    subApplication?: string;
  }
): boolean => {
  return expandedItems[item.name] || 
         expandedItems[pathIdentifier] ||
         (level === 0 && selectedPaths.industry === item.name) ||
         (level === 1 && selectedPaths.subIndustry === item.name) ||
         (level === 2 && selectedPaths.application === item.name);
};

/**
 * Check if navigation item is currently selected
 */
export const isNavigationItemSelected = (
  level: number,
  parentPath: NavigationPath,
  itemName: string,
  selectedPaths: {
    industry?: string;
    subIndustry?: string;
    application?: string;
    subApplication?: string;
  }
): boolean => {
  return level === 3 && 
         selectedPaths.industry === parentPath.industry &&
         selectedPaths.subIndustry === parentPath.subIndustry &&
         selectedPaths.application === parentPath.application &&
         selectedPaths.subApplication === itemName;
};

/**
 * Build navigation path for current item
 */
export const buildNavigationPath = (parentPath: NavigationPath, level: number, itemName: string): NavigationPath => {
  const currentPath = { ...parentPath };
  if (level === 0) currentPath.industry = itemName;
  if (level === 1) currentPath.subIndustry = itemName;
  if (level === 2) currentPath.application = itemName;
  return currentPath;
};

/**
 * Validate navigation selection completeness
 */
export const isNavigationSelectionComplete = (
  selectedIndustry?: string,
  selectedSubIndustry?: string,
  selectedApplication?: string,
  selectedSubApplication?: string
): boolean => {
  return !!(selectedIndustry && selectedSubIndustry && selectedApplication && selectedSubApplication);
};

/**
 * Get image source for position summary item
 */
export const getPositionImageSource = (alfaLavalPosition: string, milkCoolerImage: string, steamHeaterImage: string): string => {
  return alfaLavalPosition.includes("Milk") ? milkCoolerImage : steamHeaterImage;
};

/**
 * Format filename for CSV download
 */
export const formatDownloadFilename = (item: IPositionSummary): string => {
  return `${item.alfaLavalPosition}-${item.blindTestDataSetId}.csv`;
};

/**
 * Create render cards function
 */
export const createRenderCards = (
  positionSummaries: IPositionSummary[],
  activeCardId: string | null,
  downloadingItems: Record<string, boolean>,
  isModeler: boolean,
  isSme: boolean,
  handleCardClick: (cardId: string, item: IPositionSummary) => void,
  downloadCSVFile: (datasetId: string, item: IPositionSummary) => void,
  styles: any,
  PositionSummaryCard: React.ComponentType<any>
) => {
  return () => {
    if (!positionSummaries || positionSummaries.length === 0) {
      return React.createElement(EmptyResultsDisplay, { styles });
    }

    // Inject global styles
    const globalStylesElement = React.createElement('style', { 
      key: 'global-styles' 
    }, getBlindTestDashboardGlobalStyles());

    const cards = positionSummaries.map((item: IPositionSummary) => {
      const cardId = generateCardId(item);
      const isActive = activeCardId === cardId;
      const isDownloading = downloadingItems[cardId] || false;

      return React.createElement(PositionSummaryCard, {
        key: cardId,
        item,
        isActive,
        isDownloading,
        isModeler,
        isSme,
        onCardClick: () => handleCardClick(cardId, item),
        onDownload: () => 
          canDownloadCard(item)
            ? downloadCSVFile(item.blindTestDataSetId ?? "", item)
            : undefined,
        styles
      });
    });

    return [globalStylesElement, ...cards];
  };
};

/**
 * Performance utilities
 */
export const createPerformanceUtils = () => {
  /**
   * Debounce function for performance optimization
   */
  const debounce = <T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  /**
   * Throttle function for performance optimization
   */
  const throttle = <T extends (...args: any[]) => any>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let inThrottle = false;
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func(...args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, delay);
      }
    };
  };

  /**
   * Deep clone utility for state management
   */
  const deepClone = <T>(obj: T): T => {
    if (obj === null || typeof obj !== "object") return obj;
    if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
    if (obj instanceof Array) return obj.map(item => deepClone(item)) as unknown as T;
    if (typeof obj === "object") {
      const clonedObj = {} as { [key: string]: any };
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          clonedObj[key] = deepClone(obj[key]);
        }
      }
      return clonedObj as T;
    }
    return obj;
  };

  /**
   * Format performance metrics for display
   */
  const formatPerformanceMetrics = (metrics: Record<string, number>): string[] => {
    return Object.entries(metrics).map(([key, value]) => 
      `${key}: ${value.toFixed(2)}ms`
    );
  };

  return {
    debounce,
    throttle,
    deepClone,
    formatPerformanceMetrics
  };
};

/**
 * Data validation utilities
 */
export const createValidationUtils = () => {
  /**
   * Validate position summary data structure
   */
  const validatePositionSummary = (item: any): item is IPositionSummary => {
    return item &&
           typeof item === 'object' &&
           typeof item.alfaLavalPosition === 'string' &&
           typeof item.productLine === 'string' &&
           typeof item.blindTestDataSetId === 'string' &&
           item.summary &&
           typeof item.summary === 'object';
  };

  /**
   * Validate navigation item data structure
   */
  const validateNavigationItem = (item: any): item is NavItem => {
    return item &&
           typeof item === 'object' &&
           typeof item.name === 'string' &&
           (item.children === undefined || Array.isArray(item.children));
  };

  /**
   * Sanitize string for safe rendering
   */
  const sanitizeString = (str: string): string => {
    if (typeof str !== 'string') return '';
    return str.replace(/[<>]/g, '').trim();
  };

  return {
    validatePositionSummary,
    validateNavigationItem,
    sanitizeString
  };
};

/**
 * Local storage utilities specific to BlindTestDashboard
 */
export const createLocalStorageUtils = () => {
  /**
   * Save navigation state to local storage
   */
  const saveNavigationState = (
    industry: string,
    subIndustry: string,
    application: string,
    subApplication: string,
    setLocalStorageItem: (key: string, value: string) => void
  ) => {
    setLocalStorageItem('BlindTestDashboard.Industry', industry);
    setLocalStorageItem('BlindTestDashboard.SubIndustry', subIndustry);
    setLocalStorageItem('BlindTestDashboard.Application', application);
    setLocalStorageItem('BlindTestDashboard.SubApplication', subApplication);
  };

  /**
   * Load navigation state from local storage
   */
  const loadNavigationState = (
    getLocalStorageItem: (key: string) => string | null
  ): {
    industry: string | null;
    subIndustry: string | null;
    application: string | null;
    subApplication: string | null;
  } => {
    return {
      industry: getLocalStorageItem('BlindTestDashboard.Industry'),
      subIndustry: getLocalStorageItem('BlindTestDashboard.SubIndustry'),
      application: getLocalStorageItem('BlindTestDashboard.Application'),
      subApplication: getLocalStorageItem('BlindTestDashboard.SubApplication')
    };
  };

  /**
   * Clear navigation state from local storage
   */
  const clearNavigationState = (
    removeLocalStorageItem: (key: string) => void
  ) => {
    removeLocalStorageItem('BlindTestDashboard.Industry');
    removeLocalStorageItem('BlindTestDashboard.SubIndustry');
    removeLocalStorageItem('BlindTestDashboard.Application');
    removeLocalStorageItem('BlindTestDashboard.SubApplication');
  };

  return {
    saveNavigationState,
    loadNavigationState,
    clearNavigationState
  };
}; 